import { deleteShift, shiftQueryOptions, shiftKeys } from "@/api/Shifts";
import BinIcon from "@medoma/components/icons/BinIcon";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";
import styles from "./ShiftDetails.module.scss";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { PlainButton } from "@medoma/components/Button/Button";
import { Heading } from "@medoma/components/Heading/Heading";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import {
  employeeName,
  shiftProviderSchema,
  shiftTypeSchema,
  shiftTypesDictionary,
} from "@medoma/models/shifts";
import { ShiftTime } from "@/components/Time/ShiftTime";
import { ShiftDisplayName } from "./ShiftsDisplayName";
import * as Sentry from "@sentry/react";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import { Text } from "@medoma/components/Text/Text";
import { DescriptionList } from "@/components/DescriptionList/DescriptionList";
import { CloseButton } from "@/components/NavigationButtons/NavigationButtons";
import { ChangeEmployee } from "./ChangeEmployee";
import { ChangeTeam } from "./ChangeTeam";
import { ChangeShiftTimes } from "./ChangeShiftTimes";
import { ChangeShiftType } from "./ChangeShiftType";
import { useTeams } from "@/api/Patients";
import { useEmployees } from "@/api/Employee";
import { useUserProfile } from "@/api/Users";
import { shiftTypesByOrganization } from "@medoma/models/shiftTypesByOrganization";

const useShiftIdFromUrl = () => {
  const { shiftId: shiftIdAsString } = z
    .object({ shiftId: z.string() })
    .parse(useParams());
  return parseInt(shiftIdAsString, 10);
};

export const ShiftDetails = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const shiftId = useShiftIdFromUrl();
  const queryClient = useQueryClient();
  const { confirm } = useDialog();
  const { organization } = useUserProfile();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const {
    data: shift,
    isPending: isPendingShift,
    isError: isErrorShift,
    error: errorShift,
  } = useQuery(shiftQueryOptions({ shiftId }));
  const {
    data: employees,
    isPending: isPendingEmployees,
    isError: isErrorEmployees,
    error: errorEmployees,
  } = useEmployees();
  const {
    data: teams,
    isPending: isPendingTeams,
    isError: isErrorTeams,
    error: errorTeams,
  } = useTeams();

  const shiftTypes = shiftTypesByOrganization[organization];

  const { mutate: deleteShiftMutation, isPending: isDeletingShift } =
    useMutation({
      mutationFn: () => deleteShift(shiftId),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: shiftKeys.all });
        void navigate("..");
      },
      onError: (deleteError) => {
        displayErrorMessageAlert(
          `${t`Det gick inte att ta bort arbetspasset`}. ${deducedError(deleteError)}`,
        );
      },
    });

  const confirmAndDelete = async () => {
    const confirmed = await confirm({
      title: t`Ta bort arbetspass`,
      message: t`Är du säker på att du vill ta bort arbetspasset?`,
      confirmLabel: t`Ta bort`,
    });
    if (confirmed) {
      deleteShiftMutation();
    }
  };

  if (isPendingShift || isPendingTeams || isPendingEmployees) {
    return <Loading message={t`Hämtar arbetspass`} padding={24} />;
  }

  if (isErrorShift || isErrorTeams || isErrorEmployees || !shift) {
    if (isErrorShift) {
      Sentry.captureException(errorShift);
    }
    if (isErrorTeams) {
      Sentry.captureException(errorTeams);
    }
    if (isErrorEmployees) {
      Sentry.captureException(errorEmployees);
    }

    return (
      <ErrorMessage message={t`Kunde inte hämta arbetspasset`} padding={24} />
    );
  }

  const isMedomaShift = shift.provider === shiftProviderSchema.enum.medoma;
  const isQuinyxShift = shift.provider === shiftProviderSchema.enum.quinyx;

  // Display "No Type" if the shift type is Ambulating since the sites do not use this type
  const isShiftTypeAmbulating = shift.type === shiftTypeSchema.enum.Ambulating;

  return (
    <article key={shift.id}>
      <div className={styles.header}>
        <Heading level="h1">
          <Trans>Hantera</Trans>
        </Heading>
        <CloseButton close={() => navigate("..")} />
      </div>

      <section>
        <Heading level="h2" margin="bottom">
          <Trans>Arbetspass</Trans>
        </Heading>

        <DescriptionList>
          <DescriptionList.Term>
            <Trans>Kompetens</Trans>
          </DescriptionList.Term>
          <DescriptionList.Description>
            <CompetenceChip
              competence={shift.competence}
              state="neutral"
              variant="long"
              size="medium"
            />
          </DescriptionList.Description>

          <DescriptionList.Term>
            <Trans>Tid</Trans>
          </DescriptionList.Term>
          <DescriptionList.Description>
            <div className={styles.editableField}>
              <ShiftTime
                startDateTime={shift.startDateTime}
                endDateTime={shift.endDateTime}
              />
              {isMedomaShift ? <ChangeShiftTimes shift={shift} /> : null}
            </div>
          </DescriptionList.Description>

          <DescriptionList.Term>
            <Trans context="singular">Medarbetare</Trans>
          </DescriptionList.Term>
          <DescriptionList.Description>
            <div className={styles.editableField}>
              {shift.employee ? (
                <ShiftDisplayName
                  assigned
                  employeeName={employeeName(
                    {
                      firstName: shift.employee.firstName,
                      lastName: shift.employee.lastName,
                    },
                    { employeeNameFormat: "full" },
                  )}
                />
              ) : (
                <ShiftDisplayName assigned={false} />
              )}
              {isMedomaShift || isQuinyxShift ? (
                <ChangeEmployee
                  shift={shift}
                  employees={employees}
                  isQuinyxShift={isQuinyxShift}
                />
              ) : null}
            </div>
          </DescriptionList.Description>

          {shiftTypes.length > 0 ? (
            <>
              <DescriptionList.Term>
                <Trans>Typ</Trans>
              </DescriptionList.Term>
              <DescriptionList.Description>
                <div className={styles.editableField}>
                  {isShiftTypeAmbulating ? (
                    <Text
                      element="span"
                      color="no-results"
                    >{t`Ingen typ`}</Text>
                  ) : (
                    <Text element="span">
                      {t(shiftTypesDictionary[shift.type])}
                    </Text>
                  )}
                  {isMedomaShift ? (
                    <ChangeShiftType shift={shift} shiftTypes={shiftTypes} />
                  ) : null}
                </div>
              </DescriptionList.Description>
            </>
          ) : null}

          {teams.length > 0 ? (
            <>
              <DescriptionList.Term>
                <Trans>Team</Trans>
              </DescriptionList.Term>
              <DescriptionList.Description>
                <div className={styles.editableField}>
                  {shift.team ? (
                    <Text element="span">{shift.team.displayName}</Text>
                  ) : (
                    <Text
                      element="span"
                      color="no-results"
                    >{t`Inget team`}</Text>
                  )}
                  {isMedomaShift ? (
                    <ChangeTeam shift={shift} teams={teams} />
                  ) : null}
                </div>
              </DescriptionList.Description>
            </>
          ) : null}
        </DescriptionList>

        <div className={styles.deleteShift}>
          {isMedomaShift ? (
            isDeletingShift ? (
              <Loading message={t`Tar bort arbetspass`} />
            ) : (
              <PlainButton
                type="button"
                onClick={confirmAndDelete}
                disabled={isDeletingShift}
              >
                <BinIcon />
                <Trans>Ta bort arbetspass</Trans>
              </PlainButton>
            )
          ) : (
            <Text element="p" color="faded">
              {t`Arbetspasset kan inte tas bort eftersom det kommer från ett externt schemasystem` +
                "."}
            </Text>
          )}
        </div>
      </section>
    </article>
  );
};
